import { useState, useRef, useCallback } from 'react';

export type RecordingState = 'idle' | 'recording' | 'processing' | 'completed' | 'error';

export interface VoiceRecordingOptions {
  maxDuration?: number; // 最大录音时长（秒）
  minDuration?: number; // 最小录音时长（秒）
  sampleRate?: number;  // 采样率
  language?: string;    // 语音识别语言，如 'zh', 'ja', 'en'，默认自动检测界面语言
  onAudioData?: (audioData: number[]) => void; // 实时音频数据回调
  onTranscriptReceived?: (transcript: string) => void; // 转录结果回调
  onAudioReceived?: (audioBlob: Blob) => void; // 音频文件回调
  onError?: (errorType: string, params?: any) => void; // 错误回调，支持国际化
}

export interface UseVoiceRecordingReturn {
  isRecording: boolean;
  recordingState: RecordingState;
  duration: number;
  audioLevel: number;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  cancelRecording: () => void;
  audioBlob: Blob | null;
  transcript: string;
  error: string | null;
}

/**
 * 语音录制Hook
 * 提供完整的语音录制、处理和转录功能
 */
export const useVoiceRecording = (options: VoiceRecordingOptions = {}): UseVoiceRecordingReturn => {
  const {
    maxDuration = 60, // 默认最大60秒
    minDuration = 1,  // 默认最小1秒
    sampleRate = 44100,
    language, // 语音识别语言（可选）
    onAudioData,
    onTranscriptReceived,
    onAudioReceived,
    onError
  } = options;

  // 状态管理
  const [isRecording, setIsRecording] = useState(false);
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [duration, setDuration] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);

  // 录音相关引用
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const microphoneRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0); // 录音开始时间戳
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  /**
   * 获取麦克风权限并初始化录音
   */
  const initializeRecording = async (): Promise<MediaStream> => {
    try {
      // 检查浏览器支持
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('您的浏览器不支持录音功能');
      }

      // 请求麦克风权限
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      return stream;
    } catch (err) {
      let errorType = 'general';
      let errorMessage = '录音初始化失败';
      
      if (err instanceof Error) {
        // 根据错误类型提供更清晰的提示
        if (err.name === 'NotAllowedError') {
          errorType = 'NotAllowedError';
          errorMessage = '麦克风权限被拒绝，请允许使用麦克风';
        } else if (err.name === 'NotFoundError') {
          errorType = 'NotFoundError';
          errorMessage = '未找到麦克风设备';
        } else if (err.name === 'NotReadableError') {
          errorType = 'NotReadableError';
          errorMessage = '麦克风被其他应用占用';
        } else if (err.name === 'OverconstrainedError') {
          errorType = 'OverconstrainedError';
          errorMessage = '麦克风配置不支持';
        } else {
          errorMessage = err.message || '无法访问麦克风';
        }
      }
      
      setError(errorMessage);
      onError?.(errorType);
      throw new Error(errorMessage);
    }
  };

  /**
   * 设置音频分析器（用于音量检测和波形显示）
   */
  const setupAudioAnalyser = (stream: MediaStream) => {
    try {
      // 创建音频上下文
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate
      });
      
      // 创建分析器节点
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      
      // 连接麦克风到分析器
      const microphone = audioContext.createMediaStreamSource(stream);
      microphone.connect(analyser);
      
      // 保存引用
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      microphoneRef.current = microphone;
      
      // 开始音频数据分析
      startAudioAnalysis();
    } catch (err) {
      // 音频分析器设置失败，忽略错误
    }
  };

  /**
   * 开始音频数据分析（实时音量和波形）
   */
  const startAudioAnalysis = () => {
    if (!analyserRef.current) return;

    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const analyze = () => {
      if (!isRecording) return;

      analyser.getByteFrequencyData(dataArray);
      
      // 计算平均音量
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        sum += dataArray[i];
      }
      const average = sum / bufferLength;
      const normalizedLevel = Math.min(average / 128, 1); // 归一化到0-1
      
      setAudioLevel(normalizedLevel);
      
      // 传递音频数据给回调
      if (onAudioData) {
        const audioData = Array.from(dataArray).map(value => value / 255);
        onAudioData(audioData);
      }
      
      animationFrameRef.current = requestAnimationFrame(analyze);
    };

    analyze();
  };

  /**
   * 开始录音
   */
  const startRecording = useCallback(async () => {
    try {
      setError(null);
      setRecordingState('recording');
      setDuration(0);
      setAudioLevel(0);
      setTranscript('');
      setAudioBlob(null);
      audioChunksRef.current = [];
      startTimeRef.current = 0; // 重置开始时间

      // 获取媒体流
      const stream = await initializeRecording();
      mediaStreamRef.current = stream;

      // 设置音频分析器
      setupAudioAnalyser(stream);

      // 创建MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;

      // 录音开始处理
      mediaRecorder.onstart = () => {
        // 在录音真正开始时设置开始时间
        startTimeRef.current = Date.now();
      };

      // 录音数据处理
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // 录音停止处理
      mediaRecorder.onstop = async () => {
        setRecordingState('processing');
        
        // 计算实际录音时长（不依赖React状态）
        const actualDuration = startTimeRef.current > 0 ? (Date.now() - startTimeRef.current) / 1000 : 0;
        
        // 合并音频数据
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        
        // 检查录音时长（使用实际计算的时长）
        if (actualDuration < minDuration) {
          const errorMessage = `录音时长不能少于${minDuration}秒（实际录音${actualDuration.toFixed(1)}秒）`;
          setError(errorMessage);
          setRecordingState('error');
          onError?.('minDuration', { 
            minDuration: minDuration.toString(), 
            actualDuration: actualDuration.toFixed(1) 
          });
          return;
        }

        // 自动开始语音转文字处理
        try {
          await transcribeAudio(audioBlob);
        } catch (err) {
          setRecordingState('error');
        }
      };

      // 开始录音
      mediaRecorder.start(100); // 每100ms收集一次数据
      setIsRecording(true);

      // 开始计时（等待onstart事件设置startTimeRef）
      durationIntervalRef.current = setInterval(() => {
        if (startTimeRef.current > 0) {
          const elapsed = (Date.now() - startTimeRef.current) / 1000;
          setDuration(elapsed);
          
          // 达到最大时长自动停止
          if (elapsed >= maxDuration) {
            stopRecording();
          }
        }
      }, 100);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '录音启动失败';
      setError(errorMessage);
      setRecordingState('error');
      onError?.('general');
    }
  }, [maxDuration, minDuration, onAudioData, onError]);

  /**
   * 停止录音
   */
  const stopRecording = useCallback(() => {
    if (!isRecording || !mediaRecorderRef.current) return;

    try {
      // 停止录音
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      // 清理资源
      cleanup();
    } catch (err) {
      setRecordingState('error');
    }
  }, [isRecording]);

  /**
   * 取消录音
   */
  const cancelRecording = useCallback(() => {
    setIsRecording(false);
    setRecordingState('idle');
    setDuration(0);
    setAudioLevel(0);
    setAudioBlob(null);
    setTranscript('');
    setError(null);
    audioChunksRef.current = [];
    startTimeRef.current = 0; // 重置开始时间
    
    cleanup();
  }, []);

  /**
   * 清理资源
   */
  const cleanup = () => {
    // 停止计时器
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    // 停止动画帧
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // 关闭媒体流
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    // 关闭音频上下文
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // 清理其他引用
    analyserRef.current = null;
    microphoneRef.current = null;
    mediaRecorderRef.current = null;
  };

  /**
   * 语音转文字（调用真实API）
   */
  const transcribeAudio = async (audioBlob: Blob) => {
    try {
      // 动态导入语音服务，避免循环依赖
      const { voiceService } = await import('../../../services/voiceService');
      
      // 确定语音识别语言（优先级：参数 > 界面语言 > 默认日语）
      const recognitionLanguage = language || localStorage.getItem('preferred_language') || 'ja';
      
      // 调用真实的语音转录API
      const result = await voiceService.transcribeAudio(audioBlob, recognitionLanguage);
      
      // 优先使用transcription字段，备用extracted_content
      const transcript = result.transcription || result.extracted_content || '';
      
      if (!transcript.trim()) {
        throw new Error('转录结果为空，请重新录音');
      }
      
      // 语音转录成功
      
      setTranscript(transcript);
      setRecordingState('completed');
      
      // 立即调用回调，触发自动关闭弹窗和填入输入框
      onTranscriptReceived?.(transcript);
      onAudioReceived?.(audioBlob);
      
    } catch (err: any) {
      let errorMessage = '语音转文字失败';
      let errorType = 'transcribe';
      
      // 处理VoiceService的结构化错误
      if (err && typeof err === 'object' && 'type' in err) {
        errorMessage = err.message || errorMessage;
        switch (err.type) {
          case 'network':
            errorType = 'network';
            errorMessage = '网络连接失败，请检查网络连接';
            break;
          case 'format':
            errorType = 'format';
            errorMessage = '音频格式不支持，请重新录音';
            break;
          case 'server':
            errorType = 'server';
            errorMessage = '服务器处理失败，请稍后重试';
            break;
          default:
            errorType = 'transcribe';
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setRecordingState('error');
      onError?.(errorType);
    }
  };

  return {
    isRecording,
    recordingState,
    duration,
    audioLevel,
    startRecording,
    stopRecording,
    cancelRecording,
    audioBlob,
    transcript,
    error
  };
}; 